import type {
  BrandingConfig,
  GlobalBrandingChangeMessage,
  UrfBrandingColors,
} from "../types";
import { isValidHexColor } from "./colors";

function isUrfBrandingColors(value: unknown): value is UrfBrandingColors {
  if (!value || typeof value !== "object") return false;
  const colors = value as Partial<UrfBrandingColors>;
  return (
    isValidHexColor(colors.primaryColor) &&
    isValidHexColor(colors.primaryFontColor) &&
    isValidHexColor(colors.secondaryFontColor) &&
    isValidHexColor(colors.headerBackgroundColor)
  );
}

function urfColorsToBrandingPartial(
  colors: UrfBrandingColors,
): Partial<BrandingConfig> {
  return {
    themeAndColors: {
      primaryColor: colors.primaryColor.trim(),
      primaryFontColor: colors.primaryFontColor.trim(),
      secondaryFontColor: colors.secondaryFontColor.trim(),
      headerBackgroundColor: colors.headerBackgroundColor.trim(),
    },
  };
}

export type ParseGlobalBrandingResult =
  | { ok: true; config: Partial<BrandingConfig>; source: "urf-data" | "legacy-payload" }
  | { ok: false; reason: string };

/**
 * Parses parent → iframe globalBrandingChange postMessage.
 * URF spec: `{ type, data: UrfBrandingColors }`.
 * Legacy: `{ type, payload: { branding: Partial<BrandingConfig> } }`.
 */
export function parseGlobalBrandingMessage(
  message: GlobalBrandingChangeMessage,
): ParseGlobalBrandingResult {
  if (isUrfBrandingColors(message.data)) {
    return {
      ok: true,
      source: "urf-data",
      config: urfColorsToBrandingPartial(message.data),
    };
  }

  const legacyBranding = message.payload?.branding;
  if (legacyBranding && typeof legacyBranding === "object") {
    return {
      ok: true,
      source: "legacy-payload",
      config: legacyBranding,
    };
  }

  if (message.data && typeof message.data === "object") {
    return {
      ok: false,
      reason: "globalBrandingChange.data present but colors invalid or incomplete",
    };
  }

  return {
    ok: false,
    reason: "globalBrandingChange missing data (URF) and payload.branding (legacy)",
  };
}
